import React from "react";
import { Button, CircularProgress, Menu, MenuItem } from "@mui/material";
import { NavArrowDown } from "iconoir-react";
import { useAuth } from "app/authHooks";
import { hasPermission } from "core/utils/roles";
import { useNavigate } from "react-router";
import useBoolean from "../hooks/useBoolean";
import { AmlAPI } from "../api";
import _ from "lodash";
import useMessage from "../hooks/useMessage";

type AmlActionsProps = {
  personId: string;
  onActionClick?: (key: string) => void;
};

const AmlActions: React.FC<AmlActionsProps> = ({ personId, onActionClick }) => {
  const href = window.location.href;
  const isProfilePage = href.includes("/profile");
  const isTimeLinePage = href.includes("/timeline");
  const [loading, { setTrue: startLoading, setFalse: finishLoading }] =
    useBoolean();
  const message = useMessage();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const { attributes } = useAuth();
  const navigate = useNavigate();
  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(e.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const handleMenuItemClick = async (
    key: (typeof menuItems)[number]["key"]
  ) => {
    handleClose();
    if (key === "review") {
      navigate(`/investor-aml/${personId}/profile`);
    } else if (key === "assign") {
      await handleAssign();
    } else if (key === "timeline") {
      navigate(`/investor-aml/${personId}/timeline`);
    }
    onActionClick && onActionClick(key);
  };
  const handleAssign = () => {
    startLoading();
    return AmlAPI.assignToMe(personId, {
      employeeId: attributes["custom:employeeId"],
      customerId: attributes["custom:customer"],
      employeeName: attributes.name,
    })
      .then(() => {
        message.success("Investor successfully assigned to you.");
      })
      .catch((error) => {
        console.error(error);
        message.error("Unable to assign investor to you.");
        throw error;
      })
      .finally(finishLoading);
  };
  const menuItems = [
    {
      key: "review",
      label: hasPermission("update:investor-aml")
        ? "Review / Authorise"
        : "View AML Profile",
      appliable: !isProfilePage || isTimeLinePage,
    },
    {
      key: "assign",
      label: "Assign to me",
      appliable: hasPermission("update:investor-aml") && !isTimeLinePage,
    },
    {
      key: "timeline",
      label: "Timeline",
      appliable: hasPermission("view:investor-aml") && !isTimeLinePage,
    },
  ] as const;
  return (
    <>
      <Button
        variant="outlined"
        onClick={handleClick}
        size="small"
        endIcon={
          loading ? (
            <CircularProgress size="20px" />
          ) : (
            <NavArrowDown width={20} height={20} />
          )
        }
        sx={{
          borderRadius: "2px",
          padding: "1px 9px",
        }}
      >
        Actions
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{
          sx: { p: 0 },
        }}
      >
        {menuItems
          .filter((el) => el.appliable)
          .map((el) => (
            <MenuItem key={el.key} onClick={() => handleMenuItemClick(el.key)}>
              {el.label}
            </MenuItem>
          ))}
      </Menu>
    </>
  );
};

export default AmlActions;
